import {
  ArrowRightIcon,
  CalendarIcon,
  DownloadIcon,
} from '@heroicons/react/outline'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { SAVE_THE_DATE } from '../../constants/saveTheDate'

const ev = SAVE_THE_DATE.calendar
const compact = (d: string) => d.replace(/-/g, '')
const query = (params: Record<string, string>) =>
  Object.entries(params)
    .map(([k, v]) => `${k}=${encodeURIComponent(v).replace(/%2F/g, '/')}`)
    .join('&')

const OPTIONS = [
  {
    label: 'Google Calendar',
    href:
      'https://calendar.google.com/calendar/render?' +
      query({
        action: 'TEMPLATE',
        text: ev.title,
        dates: `${compact(ev.start)}/${compact(ev.end)}`,
        location: ev.location,
        details: ev.details,
      }),
    newTab: true,
  },
  {
    // A static .ics file: on iPhone/iPad/Mac it opens the Calendar app's
    // "Add event" sheet; elsewhere it downloads and opens in any calendar.
    label: 'Apple Calendar',
    href: '/save-the-date.ics',
    newTab: false,
  },
  {
    label: 'Outlook',
    href:
      'https://outlook.live.com/calendar/0/action/compose?' +
      query({
        rru: 'addevent',
        subject: ev.title,
        startdt: ev.start,
        enddt: ev.end,
        allday: 'true',
        location: ev.location,
        body: ev.details,
      }),
    newTab: true,
  },
]

type Props = {
  // Entrance timing, matched to the card coming forward.
  enterDelayMs?: number
  enterMs?: number
  easing?: string
}

// "Add to calendar" button with a small menu, shown under the opened card.
const GIF_URL = '/save-the-date.gif'
const GIF_NAME = 'Julie-and-Daniel-Save-the-Date.gif'

export const AddToCalendar = ({
  enterDelayMs = 0,
  enterMs = 600,
  easing = 'ease-out',
}: Props) => {
  const [open, setOpen] = useState(false)

  const saveImage = async (e: React.MouseEvent) => {
    const nav = navigator as Navigator & {
      canShare?: (data: { files: File[] }) => boolean
    }
    // Phones and tablets only; computers keep the plain download.
    const touch = window.matchMedia('(pointer: coarse)').matches
    if (!touch || !nav.share || !nav.canShare) return // plain download
    e.preventDefault()
    try {
      const blob = await (await fetch(GIF_URL)).blob()
      const file = new File([blob], GIF_NAME, { type: 'image/gif' })
      if (nav.canShare({ files: [file] })) {
        await nav.share({ files: [file] })
        return
      }
    } catch (err) {
      // Cancelling the share sheet is not a failure.
      if ((err as Error)?.name === 'AbortError') return
    }
    // Couldn't share: fall back to the plain download.
    const a = document.createElement('a')
    a.href = GIF_URL
    a.download = GIF_NAME
    a.click()
  }
  const rootRef = useRef<HTMLDivElement>(null)

  // Rise in alongside the card. Layout effect: starts before the first paint,
  // so the button never shows for a frame before its entrance.
  useLayoutEffect(() => {
    const el = rootRef.current
    if (!el) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const a = el.animate(
      [
        { opacity: 0, transform: 'translateY(10px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { delay: enterDelayMs, duration: enterMs, easing, fill: 'backwards' }
    )
    return () => a.cancel()
  }, [enterDelayMs, enterMs, easing])

  // Close the menu on a tap outside it.
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('pointerdown', onDown)
    return () => window.removeEventListener('pointerdown', onDown)
  }, [open])

  return (
    <div
      ref={rootRef}
      className="relative flex w-[19rem] max-w-[86vw] flex-col items-stretch sm:w-auto sm:max-w-none sm:flex-row sm:items-center sm:gap-2.5"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-haspopup="true"
          className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-[#0D7B48] bg-[#F2F1EB] px-5 py-2.5 font-label text-xs font-medium uppercase tracking-[0.22em] text-[#0D7B48] shadow-lg transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <CalendarIcon className="h-4 w-4" aria-hidden="true" />
          Add to calendar
        </button>
        {/* Save the animated card: the share sheet on phones that can share
            files ("Save Image" puts it in Photos), else a plain download. */}
        <a
          href={GIF_URL}
          download={GIF_NAME}
          onClick={saveImage}
          aria-label="Save the card as an image"
          title="Save image"
          className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border border-[#0D7B48] bg-[#F2F1EB] text-[#0D7B48] shadow-lg transition hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <DownloadIcon className="h-4 w-4" aria-hidden="true" />
        </a>
      </div>

      {/* On to the full wedding website. Phones: its own row under the
          calendar; wider screens: one row beside it. */}
      <a
        href={SAVE_THE_DATE.website}
        className="mt-2.5 flex h-[38px] items-center justify-center gap-2 rounded-full bg-[#0D7B48] px-6 font-label text-xs font-medium uppercase tracking-[0.22em] text-[#F2F1EB] shadow-lg transition hover:bg-[#0A6A3D] focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:mt-0 sm:px-5"
      >
        Visit our wedding site
        <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
      </a>

      {open && (
        <ul
          role="menu"
          className="absolute bottom-full left-0 mb-3 w-56 overflow-hidden rounded-lg bg-[#F2F1EB] py-1 text-left shadow-2xl ring-1 ring-[#0D7B48]/30"
        >
          {OPTIONS.map((o) => (
            <li key={o.label} role="none">
              <a
                role="menuitem"
                href={o.href}
                {...(o.newTab
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {})}
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 font-label text-xs font-medium uppercase tracking-[0.18em] text-[#2D1F1F] hover:bg-[#0D7B48]/10 focus:bg-[#0D7B48]/10 focus:outline-none"
              >
                {o.label}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
